import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, Loader2, ChevronLeft, Plus, Pencil, Trash2, X, Search, AlertTriangle, Package } from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { toast } from "sonner";

const ORANGE = "#EA580C";
const soles = (n) => `S/ ${Number(n || 0).toFixed(2)}`;
const UNIDADES = ["unidad", "caja", "bolsa", "kg", "m", "m2", "m3", "galón", "litro", "juego"];

const VACIO = { nombre: "", sku: "", categoria: "", unidad: "unidad", cantidad: 0, stock_minimo: 0, precio: 0 };

export default function MiInventarioPage() {
  const [data, setData] = useState(undefined);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // {mode:'crear'|'editar', form}
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState(null);

  const cargar = () => api.get("/inventario").then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { cargar(); }, []);

  const abrirCrear = () => setModal({ mode: "crear", form: { ...VACIO } });
  const abrirEditar = (it) => setModal({ mode: "editar", id: it.id, form: { nombre: it.nombre, sku: it.sku, categoria: it.categoria, unidad: it.unidad, cantidad: it.cantidad, stock_minimo: it.stock_minimo, precio: it.precio } });

  const guardar = async () => {
    const f = modal.form;
    if (!f.nombre.trim()) { toast.error("El nombre es obligatorio."); return; }
    const payload = { ...f, cantidad: Number(f.cantidad) || 0, stock_minimo: Number(f.stock_minimo) || 0, precio: Number(f.precio) || 0 };
    setGuardando(true);
    try {
      if (modal.mode === "crear") await api.post("/inventario", payload);
      else await api.put(`/inventario/${modal.id}`, payload);
      toast.success(modal.mode === "crear" ? "Producto agregado." : "Producto actualizado.");
      setModal(null);
      await cargar();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este producto del inventario?")) return;
    setBorrando(id);
    try { await api.delete(`/inventario/${id}`); await cargar(); toast.success("Producto eliminado."); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setBorrando(null); }
  };

  const items = (data?.items || []).filter((i) => {
    const ql = q.trim().toLowerCase();
    return !ql || i.nombre.toLowerCase().includes(ql) || i.sku.toLowerCase().includes(ql) || i.categoria.toLowerCase().includes(ql);
  });
  const r = data?.resumen;
  const setF = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));

  return (
    <div className="px-5 md:px-8 py-8 max-w-5xl" data-testid="mi-inventario-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Ecosistema
      </Link>
      <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3">
            <Boxes size={30} style={{ color: ORANGE }} /> Mi inventario
          </h1>
          <p className="mt-2 text-slate-600">Gestiona el stock y los materiales de tu ferretería.</p>
        </div>
        <button onClick={abrirCrear} data-testid="inventario-agregar" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold hover:brightness-110" style={{ backgroundColor: ORANGE }}>
          <Plus size={18} /> Agregar producto
        </button>
      </div>

      {r && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["Productos", r.total], ["Bajo stock", r.bajo_stock], ["Categorías", r.categorias], ["Valor total", soles(r.valor_total)]].map(([l, v], i) => (
            <div key={l} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 text-center">
              <p className="text-2xl font-extrabold" style={{ color: i === 1 && r.bajo_stock > 0 ? "#DC2626" : "#0F172A" }}>{v}</p>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="inventario-buscar" placeholder="Buscar por nombre, SKU o categoría…"
          className="w-full rounded-2xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
      </div>

      <div className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        {data === undefined && <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="animate-spin" size={16} /> Cargando inventario…</div>}
        {data === null && <p className="text-sm text-slate-400 p-8">No se pudo cargar el inventario.</p>}
        {data && items.length === 0 && (
          <div className="text-center py-16" data-testid="inventario-vacio">
            <Package size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">{q ? "Sin resultados." : "Aún no tienes productos. Agrega el primero."}</p>
          </div>
        )}
        {data && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-3">Producto</th>
                  <th className="px-4 py-3">Categoría</th>
                  <th className="px-4 py-3 text-right">Stock</th>
                  <th className="px-4 py-3 text-right">Precio</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map((it) => (
                  <tr key={it.id} className={it.bajo_stock ? "bg-red-50/40" : ""} data-testid={`inventario-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{it.nombre}</p>
                      {it.sku && <p className="text-xs text-slate-400">SKU: {it.sku}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{it.categoria || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-slate-900">{it.cantidad}</span> <span className="text-xs text-slate-400">{it.unidad}</span>
                      {it.bajo_stock && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full text-red-600 bg-red-100" data-testid={`inventario-bajo-${it.id}`}>
                          <AlertTriangle size={10} /> Bajo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{soles(it.precio)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => abrirEditar(it)} data-testid={`inventario-editar-${it.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
                        <button onClick={() => eliminar(it.id)} disabled={borrando === it.id} data-testid={`inventario-eliminar-${it.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50">
                          {borrando === it.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="inventario-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            <h3 className="text-lg font-extrabold text-slate-900">{modal.mode === "crear" ? "Agregar producto" : "Editar producto"}</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nombre *</label>
                <input value={modal.form.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="inventario-form-nombre" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" placeholder="Ej. Cemento Portland 42.5kg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">SKU / Código</label>
                  <input value={modal.form.sku} onChange={(e) => setF("sku", e.target.value)} data-testid="inventario-form-sku" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" placeholder="Opcional" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Categoría</label>
                  <input value={modal.form.categoria} onChange={(e) => setF("categoria", e.target.value)} data-testid="inventario-form-categoria" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" placeholder="Ej. Cementos" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Cantidad</label>
                  <input type="number" min="0" value={modal.form.cantidad} onChange={(e) => setF("cantidad", e.target.value)} data-testid="inventario-form-cantidad" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Unidad</label>
                  <select value={modal.form.unidad} onChange={(e) => setF("unidad", e.target.value)} data-testid="inventario-form-unidad" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200">
                    {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Stock mínimo</label>
                  <input type="number" min="0" value={modal.form.stock_minimo} onChange={(e) => setF("stock_minimo", e.target.value)} data-testid="inventario-form-minimo" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
                  <p className="text-[11px] text-slate-400 mt-1">Alerta cuando el stock sea igual o menor.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Precio (S/)</label>
                  <input type="number" min="0" step="0.01" value={modal.form.precio} onChange={(e) => setF("precio", e.target.value)} data-testid="inventario-form-precio" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={guardar} disabled={guardando} data-testid="inventario-guardar" className="px-5 py-2 rounded-lg text-white font-bold text-sm hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2" style={{ backgroundColor: ORANGE }}>
                {guardando && <Loader2 size={15} className="animate-spin" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
